/**
 * Teams Page - Jira-like Teams management interface
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { teamAPI } from '@/api/teamAPI';
import { teamMemberAPI } from '@/api/teamMemberAPI';
import { projectAPI } from '@/api/projectAPI';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { Team, TeamMember } from '@/types';
import { Users, Search, Plus, Filter, Trash2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { TeamModal } from '@/components/TeamModal/TeamModal';

export const Teams = () => {
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamMembers, setTeamMembers] = useState<{ [teamId: string]: TeamMember[] }>({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load teams and their data
  useEffect(() => {
    loadTeams();
  }, [currentUser?.organization_id]);


  const loadTeams = async () => {
    try {
      setLoading(true);
      const organizationId = currentUser?.organization_id;
      const fetchedTeams = await teamAPI.getTeams(
        organizationId ? { organization_id: organizationId } : undefined
      );
      setTeams(fetchedTeams);

      await Promise.all(fetchedTeams.map((team) => loadTeamData(team.id)));
    } catch (error) {
      console.error('Failed to load teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamData = async (teamId: string) => {
    try {
      const members = await teamMemberAPI.getTeamMembers(teamId);
      setTeamMembers(prev => ({ ...prev, [teamId]: members }));
    } catch (error) {
      console.error(`Failed to load data for team ${teamId}:`, error);
      toast.error(`Failed to load data for team ${teamId}`);
    }
  };
  // Legacy invitation helpers kept for potential future use but currently unused

  // Filter teams by search query
  const filteredTeams = teams.filter(team =>
    team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    team.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDeleteTeam = async () => {
    if (!teamToDelete) return;

    try {
      setIsDeleting(true);
      
      // Get all projects assigned to this team before deletion
      let projectIds: string[] = [];
      try {
        const teamProjects = await projectAPI.getProjects({ team_id: teamToDelete.id, is_active: true, limit: 100 });
        projectIds = teamProjects.map(p => p.id).filter(Boolean);
      } catch (error) {
        console.error('Failed to fetch team projects:', error);
      }
      
      // Delete the team
      await teamAPI.deleteTeam(teamToDelete.id);
      
      // Invalidate project member cache for all projects that were in this team
      // This ensures ProjectMembers.tsx shows updated data immediately
      projectIds.forEach(projectId => {
        projectMemberAPI.invalidateCache(projectId);
      });
      
      toast.success('Team deleted successfully');
      setShowDeleteModal(false);
      setTeamToDelete(null);
      await loadTeams();
    } catch (error: any) {
      console.error('Failed to delete team:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteModal = (team: Team, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking delete
    setTeamToDelete(team);
    setShowDeleteModal(true);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="w-8 h-8" />
            Teams
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage your teams and add people to collaborate
          </p>
        </div>
        <button
          onClick={() => setShowTeamModal(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create Team
        </button>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search teams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <button className="btn btn-outline flex items-center gap-2">
          <Filter className="w-4 h-4" />
          Filter
        </button>
      </div>

      {/* Teams Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-2 text-gray-600">Loading teams...</p>
        </div>
      ) : filteredTeams.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No teams yet</h3>
          <p className="text-gray-600 mb-4">Create your first team to start collaborating</p>
          <button
            onClick={() => setShowTeamModal(true)}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create Your First Team
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTeams.map((team) => {
            const members = teamMembers[team.id] || [];
            return (
              <div
                key={team.id}
                className="group relative flex flex-col gap-6 cursor-pointer bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
                onClick={() => navigate(`/teams/${team.id}`)}
              >
                {/* Delete Button - Shows on hover */}
                {currentUser?.role === 'admin' && (
                  <button
                    onClick={(e) => openDeleteModal(team, e)}
                    className="absolute top-4 right-4 p-2 rounded-lg bg-red-50 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 z-10"
                    title="Delete team"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                
                {/* Team Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{team.name}</h3>
                      <p className="text-sm text-gray-600">{team.description || 'No description'}</p>
                    </div>
                  </div>
                </div>

                {/* Recent Members */}
                <div className="flex items-start justify-between">
                  <div className="text-center p-3 bg-gray-50 rounded-lg flex flex-col gap-4 flex-1">
                    <div className='flex items-center gap-1 text-lg font-semibold text-gray-800'>
                    <div className="">{members.length}</div>
                    <div className="">Members</div>
                    </div>

                    
                  <div className="flex items-start gap-0.5">
                      {members.slice(0, 5).map((member, index) => {
                        const title =
                          member.user_name ||
                          member.user_email ||
                          member.user?.username ||
                          member.user?.email ||
                          'User';
                        const initialSource =
                          member.user_name ||
                          member.user_email ||
                          member.user?.display_name ||
                          member.user?.email ||
                          'U';
                        const initial = initialSource.charAt(0).toUpperCase();

                        return (
                          <div
                            key={member.id}
                            className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-medium border-2 border-white"
                            style={{ zIndex: 5 - index }}
                            title={title}
                          >
                            {initial}
                          </div>
                        );
                      })}
                      {members.length > 5 && (
                        <div className="w-8 h-8 rounded-full bg-gray-500 text-white flex items-center justify-center text-xs font-medium border-2 border-white">
                          +{members.length - 5}
                        </div>
                      )}
                  </div>
                    
                  </div>                  
                  
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Team Modal */}
      {showTeamModal && (
        <TeamModal
          isOpen={showTeamModal}
          onClose={() => setShowTeamModal(false)}
          onCreated={async () => {
            await loadTeams();
          }}
          defaultOrganizationId={currentUser?.organization_id}
        />
      )}

      {/* Delete Team Confirmation Modal */}
      {showDeleteModal && teamToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Team
            </h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete the team
              <span className="font-medium"> {teamToDelete.name}</span>?
              <br />
              <span className="text-sm text-gray-500 mt-2 block">
                This action cannot be undone.
              </span>
            </p>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-700"
                onClick={() => {
                  setShowDeleteModal(false);
                  setTeamToDelete(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-2"
                disabled={isDeleting}
                onClick={handleDeleteTeam}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
